// strings.ts — English defaults for the trial-beta LP's HUD copy (spec D-4).
//
// Extracted from the design attachment's animation scripts (lines 4136-7350) with
// `grep -oE "'[A-Z][A-Za-z0-9 %+.,·—'-]{3,45}'"` as instructed, which yielded 165 raw
// matches. That count is not the same as 165 dictionary entries here, for two
// reasons verified against the source:
//   1. The regex's character class is greedy across embedded quotes, so several
//      array literals (game names, CDN provider names, edge labels) were captured
//      as one bundled match spanning multiple comma-separated items rather than
//      one match per item. Those are restored to their real array form below
//      (e.g. `premiumExperience.gameNames`), which is more entries than raw matches.
//   2. A handful of matches are DOM/keyboard-event tokens, not user-visible copy:
//      'ArrowLeft', 'ArrowRight', 'Enter', 'Escape', 'Home', 'DOMContentLoaded',
//      'IntersectionObserver'. Those are excluded here.
// Static HTML text embedded in template-literal (backtick) markup was never in the
// regex's scope (it only matches single-quoted JS string literals) and is not
// covered by this dictionary — it belongs to each section's JSX/i18n, same as any
// other LP copy.
//
// Interpolated strings (built by concatenation in the original) become functions,
// matching spec's `packetLoss` example — callers pass the live value, this module
// owns the exact copy/punctuation.
//
// Grouped by originating `NP.register(...)` slug where one could be identified from
// context; a few clusters that render supporting markup for a later register call
// (e.g. the "how it works" step-card preview) are grouped with the section they
// visually belong to rather than the nearest slug.

export interface MultiInternetEventCopy {
  healthy: string;
  degrading: string;
  detected: string;
  failover: string;
  recovering: string;
  restored: string;
}

export interface PremiumMultiInternetUplink {
  name: string;
  edges: [string, string, string];
}

// The multiconnection instrument's event bar renders one sentence out of several coloured
// fragments (`.event-message .old` / `.new` / `.spike`). The source built that sentence as an
// innerHTML string; here the copy stays plain text and the module builds the spans itself, so
// each message is a list of fragments with an optional tone rather than markup.
export type MultiConnectionEventPart = { text: string; tone?: 'old' | 'new' | 'spike' };

export interface MultiInternetScenarioCopy {
  indicator: string;
  title: string;
  detail: string;
  aria: string;
}

export type MultiInternetScenarioKey = 'restored' | 'i1' | 'i2' | 'i3' | 'i12' | 'i13' | 'i23';

export interface MultiInternetGuideStage {
  label: string;
  action: string;
}

// p6-standalone.js's step-2 game rail (source 5745, `games` array): short is what the status
// line prints ("CS2 QUEUED"/"CS2 SELECTED"), name is the big label under the cover rail.
export interface ExperienceShowcaseGame {
  name: string;
  short: string;
}

export interface HudStrings {
  common: {
    connect: string;
    disconnect: string;
    ready: string;
    online: string;
    offline: string;
    active: string;
    selected: string;
    stable: string;
    unstable: string;
    excellent: string;
    poor: string;
    improving: string;
    optimizing: string;
    optimized: string;
    recovering: string;
    calculating: string;
    downloadingPercent: (percent: number) => string;
    installReady: string;
  };
  hero: {
    testing5Routes: string;
    scanningRoutes: string;
    smartReroute: string;
    reroutingPackets: string;
    routeLocked: string;
    optimalRoute: string;
    baselineDetected: string;
    baselineCapture: string;
    chartTitle: string;
    chartEngine: string;
    chartBefore: string;
    chartAfter: string;
  };
  multiConnection: {
    accessNames: [string, string, string];
    providers: string[];
    primaryLabel: string;
    onlineMs: (ms: number) => string;
    measuring9Routes: string;
    aiElectionInProgress: string;
    fastestPathLocked: string;
    testingRoutesOfNine: (tested: number) => string;
    aiElectingFastestPath: string;
    waitingForRouteLock: string;
    latencySaved: (savedPercent: number, savedMs: number) => string;
    calculatingSavings: string;
  };
  // Telemetry the multiconnection INSTRUMENT writes at runtime (attachment lines 2743-2808 — the
  // script of the flattened iframe-1 document). Deliberately separate from `multiConnection`
  // above: that group was extracted from the premium 9-route animation script (attachment
  // 4136-7350) and describes a different widget; this one is the 5-route Dallas/Washington
  // instrument and shares none of its copy. Only strings the script *writes* live here — the
  // instrument's static, no-JS copy is ordinary page copy and goes through `t(...)` in
  // ../sections/multiconnection.tsx, the same split hero.tsx/hero.ts already use.
  multiConnectionInstrument: {
    bridgeStateConnected: string;
    bridgeStateSwapping: (seconds: string) => string;
    routeLabel: (name: string, access: number, edge: number, total: number) => string;
    routeSwappingLabel: (oldName: string, nextName: string) => string;
    hopOffline: string;
    hopLabel: (name: string, latency: number) => string;
    hopOfflineLabel: (name: string) => string;
    gameLive: string;
    gameOffline: string;
    gameDegraded: string;
    gameplayLabel: string;
    gameplaySlowedLabel: string;
    gameplayFrozenLabel: string;
    topologyLabelNoping: string;
    topologyLabelIsp: string;
    flowMiddleBridges: string;
    flowMiddleIsp: string;
    modeDescriptionNoping: string;
    modeDescriptionIsp: string;
    eventKickerNoping: string;
    eventKickerIsp: string;
    eventMessageNoping: string;
    eventMessageIsp: string;
    lockCopyNoping: string;
    lockCopyIsp: string;
    lockReadoutBridge: (name: string, total: number) => string;
    lockReadoutIsp: (total: number) => string;
    lockReadoutLost: string;
    activeCountBridges: (online: number) => string;
    activeCountIsp: string;
    activeCountLost: string;
    legendActiveRoute: string;
    legendReplacingBridge: string;
    legendSingleIspPath: string;
    legendOfflineHop: string;
    // The uptime footer is one sentence with two <b> runs and an aria-hidden separator; the
    // module assembles those nodes, so the copy arrives here already split at the tag seams.
    uptimeNoping: { lead: string; strong: string; separator: string; middle: string; strongTail: string };
    uptimeIsp: { strongLead: string; separator: string; tail: string };
    routeSwapped: (oldName: string, nextName: string, online: number) => MultiConnectionEventPart[];
    routeRestored: (name: string) => MultiConnectionEventPart[];
    routeRestoredWhileSwapping: (name: string, online: number, pendingOldName: string, pendingNextName: string) => MultiConnectionEventPart[];
    ispDisconnected: (hopName: string) => MultiConnectionEventPart[];
    ispRestored: () => MultiConnectionEventPart[];
    ispSpike: (spikeMs: number, hopName: string) => MultiConnectionEventPart[];
    reducedMotionNoping: string;
    reducedMotionIsp: string;
  };
  // Telemetry the multi-internet INSTRUMENT writes at runtime (attachment lines 3147-3459 — the
  // script of the flattened iframe-2 document). A third sibling of `multiInternet` and
  // `premiumMultiInternet` below, for the same reason `multiConnectionInstrument` is a sibling of
  // `multiConnection`: those two describe other widgets (a 2-uplink Fiber/Cable panel and the
  // premium 9-route animation) and share none of this 3x3 mesh's copy. Only strings the script
  // *writes* live here — the instrument's static, no-JS copy is ordinary page copy and goes
  // through `t(...)` in ../sections/multi-internet.tsx.
  multiInternetInstrument: {
    netStateOnline: string;
    netStateInterrupted: string;
    bridgeStateLive: (server: string) => string;
    bridgeStateNoSignal: (server: string) => string;
    latencyOffline: string;
    netGroupLabel: (label: string) => string;
    netOnlineLabel: (label: string) => string;
    netInterruptedLabel: (label: string) => string;
    bridgeGroupLabel: (server: string, location: string, netLabel: string) => string;
    bandTitle: (label: string) => string;
    routeGroupTitle: (groupNumber: number) => string;
    computerGroupLabel: string;
    computerTitle: string;
    computerSub: string;
    hubGroupLabel: string;
    hubTitle: string;
    hubSub: string;
    gameGroupLabel: string;
    gameTitle: string;
    gameSub: string;
    gameLive: string;
    gameplayLabel: string;
    routesLive: (routes: number) => string;
    packetsForwarded: string;
    activeLinks: (active: number) => string;
    activeRoutes: (routes: number) => string;
    systemStateFull: string;
    systemStateDegraded: string;
    scenarios: Record<MultiInternetScenarioKey, MultiInternetScenarioCopy>;
    guideStages: [MultiInternetGuideStage, MultiInternetGuideStage, MultiInternetGuideStage];
    reducedMotionAria: string;
  };
  multiInternet: {
    ispInfo: Array<{ label: string; speed: string; latency: string }>;
    phaseCopy: MultiInternetEventCopy;
    eventCopy: MultiInternetEventCopy;
    sync: string;
    noInterruption: string;
    packetsDroppedNone: string;
    carryingTraffic: string;
    activeMobile: string;
  };
  beforeAfter: {
    comparisonDivider: string;
    // Source 4842-4863 (`ba-charts`): both panes reuse this exact "Packet loss: X.X%" / "Jitter:
    // Xms" phrasing, but the bad side rounds jitter to whole ms while the good side keeps one
    // decimal — `decimals` lets one function serve both instead of two near-duplicate strings.
    packetLoss: (percent: number) => string;
    jitter: (ms: number, decimals: number) => string;
  };
  experienceShowcase: {
    serverRows: string[];
    connect: string;
    disconnect: string;
    scanningRoutes: string;
    resettingRoute: string;
    scanning9Routes: string;
    testing9Servers: string;
    selectBestRoute: string;
    routeLockedMs: string;
    // The five fields above readyToDownload were verified against p6-standalone.js (the
    // #how-it-works section's real, live animation — task 11): the download/install and ping
    // status strings that module writes each frame. The five that come before belong to a
    // DIFFERENT, still-dead mobile-client mock in anim-p3.js (5044-5941ish) that happens to share
    // this bucket per the header comment's "grouped by the section they visually belong to" rule.
    readyToDownload: string;
    downloadStarted: string;
    pingOffline: string;
    pingOnline: string;
    loweringPingBoostingFps: string;
    // Added for task 11 (not from the raw grep — its regex requires a leading capital letter, so
    // it never matched digit-led literals like '9MS PING · 164 FPS', and 'CS2 QUEUED'/'... SELECTED'
    // were built by concatenation in the source, not stored as a single literal it could capture).
    /** Source 5745: the 4-game rail, in `data-game-cover`/`data-game-button` order. */
    games: readonly ExperienceShowcaseGame[];
    /** `${short} QUEUED` — source 5794 (`gameStatus.textContent` before the game is auto-selected). */
    gameQueued: (short: string) => string;
    /** `${short} SELECTED` — source 5794 and the manual-click handler (5872). */
    gameSelected: (short: string) => string;
    /** Source 5809's `'9MS PING · 164 FPS'`, generalised over the settled ping/fps pair. */
    pingLockedFps: (ms: number, fps: number) => string;
    /** Source's own `'--'` placeholder for ping/jitter/loss while the step-3 demo is offline. */
    offlinePlaceholder: string;
  };
  gamesShowcase: {
    connectionUnoptimized: string;
    activatingNoping: string;
    nopingOptimized: string;
    optimizeGame: (gameName: string) => string;
  };
  // Verified against the source (task 11): every string below was extracted from anim-p4.js
  // 6140-6142, inside a block gated by `if (hasLegacyHiw)` where `hasLegacyHiw` requires
  // `#how-it-works [data-anim="hiw-step"]` to have exactly 3 matches (5879-5881). The shipped
  // #how-it-works markup (the "p6" redesign) has zero such elements, so this whole group is dead
  // code the live page never reaches — #how-it-works's real runtime copy lives in
  // `experienceShowcase` instead (p6-standalone.js). Left here, unused, as documented scaffolding
  // rather than deleted, in case a future task finds the legacy structure has a use after all.
  howItWorks: {
    stepPreviewStatuses: [string, string, string];
    waitingForInstall: string;
    selectingValorant: string;
    valorantSelected: string;
    waitingForGame: string;
    testingRouteOfNine: (routeNumber: number) => string;
    routeLockedNineMs: string;
  };
  premiumHeroPingDrop: {
    clientChapters: { selectFortnite: string; buildingSmartRoute: string; sessionConnected: string; liveTrafficOptimized: string };
    rawLatency: string;
    smartPending: string;
    smartMs: (value: number) => string;
    baselineCapture: string;
    unstable: string;
    scanning9RoutesSentence: string;
    poor: string;
    probingEdges: string;
    optimizingLive: string;
    rerouting: string;
    improving: string;
    bypassingLag: string;
    bypassingCongestion: string;
    routeLocked: string;
    stable: string;
    directPeering: string;
    excellent: string;
    pathLocked: string;
    optimalPathLocked: string;
  };
  premiumMultiConnection: {
    providers: string[];
    discoveringRoutes: string;
    calculating: string;
    waiting: string;
    probeOfNine: (probeNumber: number) => string;
    awaitingRouteLock: string;
    evaluatingCandidates: string;
    aiElection: string;
    negotiating: string;
    threeViablePaths: string;
    consensusInProgress: string;
    fastestPathLocked: string;
    edgeLockedNineMs: string;
  };
  premiumMultiInternet: {
    accessNames: [string, string, string];
    uplinks: Record<'i1' | 'i2' | 'i3', PremiumMultiInternetUplink>;
    scenarioTitles: {
      reducedMotion: string;
      internet1Offline: string;
      internet1Recovered: string;
      internet3Offline: string;
      internet3Recovered: string;
      internet2And3Offline: string;
      fullRedundancyRestored: string;
    };
    offlineFailed: string;
    onlineActive: string;
    isolatedNoSignal: string;
    activeLatency: (latency: string | number) => string;
    failoverActiveGameOnline: string;
    fullRedundancyOnline: string;
    pauseMotion: string;
    resumeMotion: string;
  };
  premiumExperience: {
    gameNames: string[];
    sceneCaptions: string[];
    servers: Array<{ name: string; edge: string }>;
    ready: string;
    selected: string;
    connectNow: string;
    optimizing: string;
    connectedMs: (ping: number) => string;
    connected: string;
    disconnected: string;
    edgeLabel: (edge: string) => string;
    edgeWinsMs: (edge: string, ping: number) => string;
    routeLocked: string;
    routeRace: string;
    testing5PathsInParallel: string;
    sessionProof: string;
    searching: string;
    optimizeGameUpper: (gameName: string) => string;
  };
}

export const HUD_STRINGS_EN: HudStrings = {
  common: {
    connect: 'CONNECT',
    disconnect: 'DISCONNECT',
    ready: 'READY',
    online: 'ONLINE',
    offline: 'OFFLINE',
    active: 'ACTIVE',
    selected: 'SELECTED',
    stable: 'STABLE',
    unstable: 'UNSTABLE',
    excellent: 'EXCELLENT',
    poor: 'POOR',
    improving: 'IMPROVING',
    optimizing: 'OPTIMIZING',
    optimized: 'OPTIMIZED',
    recovering: 'RECOVERING',
    calculating: 'CALCULATING',
    downloadingPercent: (percent) => `DOWNLOADING ${Math.round(percent)}%`,
    installReady: 'INSTALL READY',
  },
  hero: {
    testing5Routes: 'TESTING 5 ROUTES',
    scanningRoutes: 'SCANNING ROUTES',
    smartReroute: 'SMART REROUTE',
    reroutingPackets: 'REROUTING PACKETS',
    routeLocked: 'ROUTE LOCKED',
    optimalRoute: 'OPTIMAL ROUTE',
    baselineDetected: 'BASELINE DETECTED',
    baselineCapture: 'BASELINE CAPTURE',
    chartTitle: 'LIVE LATENCY / LOS ANGELES → GAME SERVER',
    chartEngine: 'AI ROUTE ENGINE',
    chartBefore: 'UNOPTIMIZED',
    chartAfter: 'OPTIMIZED',
  },
  multiConnection: {
    accessNames: ['INTERNET A', 'INTERNET B', 'WIFI 5G'],
    providers: ['GOOGLE CLOUD', 'AKAMAI', 'AZURE', 'GCORE', 'EQUINIX', 'AWS', 'SOFTLAYER', 'LEASEWEB', 'CLOUDFLARE'],
    primaryLabel: 'PRIMARY · 9ms',
    onlineMs: (ms) => `ONLINE · ${ms}ms`,
    measuring9Routes: 'MEASURING 9 ROUTES',
    aiElectionInProgress: 'AI ELECTION IN PROGRESS',
    fastestPathLocked: 'FASTEST PATH LOCKED',
    testingRoutesOfNine: (tested) => `TESTING ${tested} OF 9 ROUTES`,
    aiElectingFastestPath: 'AI ELECTING FASTEST PATH',
    waitingForRouteLock: 'Waiting for route lock',
    latencySaved: (savedPercent, savedMs) => `−${savedPercent}% LATENCY · ${savedMs}MS SAVED`,
    calculatingSavings: 'CALCULATING SAVINGS…',
  },
  multiConnectionInstrument: {
    bridgeStateConnected: 'Connected · packets live',
    bridgeStateSwapping: (seconds) => `Link lost · swapping ${seconds}s`,
    routeLabel: (name, access, edge, total) => `${name}, active bridge, ${access} milliseconds to the bridge, ${edge} milliseconds to the NoPing node, ${total} milliseconds total`,
    routeSwappingLabel: (oldName, nextName) => `${oldName} offline, replacing with ${nextName}`,
    hopOffline: 'OFFLINE',
    hopLabel: (name, latency) => `${name}, ${latency} milliseconds`,
    hopOfflineLabel: (name) => `${name} offline, connection interrupted`,
    gameLive: 'LIVE',
    gameOffline: 'OFFLINE',
    gameDegraded: 'DEGRADED',
    gameplayLabel: 'Stylized battle royale gameplay preview',
    gameplaySlowedLabel: 'Slowed Fortnite gameplay preview on the default ISP route',
    gameplayFrozenLabel: 'Fortnite gameplay frozen because the ISP route disconnected',
    topologyLabelNoping: 'Packets traveling through five simultaneous NoPing bridges to the game server',
    topologyLabelIsp: 'A single ISP route from Dallas through seven hops to the game server',
    flowMiddleBridges: '5 live bridges',
    flowMiddleIsp: '1 ISP route',
    modeDescriptionNoping: 'Five-route protection is active',
    modeDescriptionIsp: 'Default ISP routing — no backup path',
    eventKickerNoping: 'Multi-connections',
    eventKickerIsp: 'Default ISP route',
    eventMessageNoping: 'Monitoring five Dallas/Washington bridges — packets are flowing on every route.',
    eventMessageIsp: 'One route from Dallas to Washington — every hop is a single point of failure.',
    lockCopyNoping: 'Always routing through the lowest-latency bridge',
    lockCopyIsp: 'Single ISP route total',
    lockReadoutBridge: (name, total) => `${name} · ${total}ms total`,
    lockReadoutIsp: (total) => `Dallas → Washington · ${total}ms total`,
    lockReadoutLost: 'Connection lost',
    activeCountBridges: (online) => `${online} / 5`,
    activeCountIsp: '1 / 1',
    activeCountLost: '0 / 1',
    legendActiveRoute: 'Active route',
    legendReplacingBridge: 'Replacing bridge',
    legendSingleIspPath: 'Single ISP path',
    legendOfflineHop: 'Offline hop = disconnect',
    uptimeNoping: { lead: 'Session continuity ', strong: '100%', separator: '//', middle: ' at least ', strongTail: '1 route always online' },
    uptimeIsp: { strongLead: 'Route redundancy 0', separator: '//', tail: ' one failure interrupts the session' },
    routeSwapped: (oldName, nextName, online) => [{ text: oldName, tone: 'old' }, { text: ' → ' }, { text: nextName, tone: 'new' }, { text: ` · rerouting · ${online}/5 routes live` }],
    routeRestored: (name) => [{ text: `${name} online`, tone: 'new' }, { text: ' · replacement verified · five-route mesh restored' }],
    routeRestoredWhileSwapping: (name, online, pendingOldName, pendingNextName) => [{ text: `${name} online`, tone: 'new' }, { text: ` · ${online}/5 live · ` }, { text: pendingOldName, tone: 'old' }, { text: ' → ' }, { text: pendingNextName, tone: 'new' }, { text: ' still swapping' }],
    ispDisconnected: (hopName) => [{ text: 'You were disconnected.', tone: 'old' }, { text: ` ${hopName} went offline — one ISP route means no backup.` }],
    ispRestored: () => [{ text: 'Connection restored.', tone: 'new' }, { text: ' Your ISP route is online again, but there is still no backup.' }],
    ispSpike: (spikeMs, hopName) => [{ text: `You just got a ping spike of +${spikeMs}ms.`, tone: 'spike' }, { text: ` ${hopName} added latency.` }],
    reducedMotionNoping: 'Five active bridges — automatic failover available. Motion is reduced by your system preference.',
    reducedMotionIsp: 'Single ISP route displayed. Motion is reduced by your system preference.',
  },
  multiInternetInstrument: {
    netStateOnline: 'ONLINE · LIVE',
    netStateInterrupted: 'INTERRUPTED',
    bridgeStateLive: (server) => `${server} · LIVE`,
    bridgeStateNoSignal: (server) => `${server} · NO SIGNAL`,
    latencyOffline: '—',
    netGroupLabel: (label) => `${label}, three dedicated NoPing servers`,
    netOnlineLabel: (label) => `${label}, online; packets are flowing through its three NoPing servers`,
    netInterruptedLabel: (label) => `${label}, interrupted; NoPing continues through the remaining live internet links`,
    bridgeGroupLabel: (server, location, netLabel) => `${server}, ${location}, assigned to ${netLabel}`,
    bandTitle: (label) => `${label} · 3 SERVERS`,
    routeGroupTitle: (groupNumber) => `ROUTE GROUP 0${groupNumber}`,
    computerGroupLabel: 'Your computer, origin of the game packets',
    computerTitle: 'YOUR COMPUTER',
    computerSub: 'GAME PACKETS LIVE',
    hubGroupLabel: 'Final NoPing server, packet aggregator for all nine upstream servers',
    hubTitle: 'FINAL NOPING',
    hubSub: 'PACKET AGGREGATOR',
    gameGroupLabel: 'Fortnite game server, connected',
    gameTitle: 'FORTNITE',
    gameSub: 'CONNECTED',
    gameLive: 'LIVE',
    gameplayLabel: 'Fortnite gameplay running',
    routesLive: (routes) => `${routes} ROUTES LIVE`,
    packetsForwarded: '1 FORWARDED',
    activeLinks: (active) => `${active} / 3`,
    activeRoutes: (routes) => `${routes} / 9`,
    systemStateFull: 'All redundant links active · game online',
    systemStateDegraded: 'Redundant links active · disconnect prevented',
    scenarios: {
      restored: {
        indicator: 'ALL LINKS ACTIVE',
        title: 'FULL REDUNDANCY RESTORED',
        detail: 'All 3 internets and all 9 NoPing servers are transmitting packets.',
        aria: 'All three internet links are online. Packets are flowing through all nine NoPing servers to the game.',
      },
      i1: {
        indicator: 'A INTERRUPTED',
        title: 'INTERNET A INTERRUPTED · GAME STAYED ONLINE',
        detail: 'NoPing kept transmitting through Internet B + C. Without simultaneous links, the game would have disconnected.',
        aria: 'Internet A was interrupted. NoPing continued transmitting through Internets B and C and prevented the game from disconnecting.',
      },
      i2: {
        indicator: 'B INTERRUPTED',
        title: 'INTERNET B INTERRUPTED · GAME STAYED ONLINE',
        detail: 'NoPing kept transmitting through Internet A + C. Without simultaneous links, the game would have disconnected.',
        aria: 'Internet B was interrupted. NoPing continued transmitting through Internets A and C and prevented the game from disconnecting.',
      },
      i3: {
        indicator: 'C INTERRUPTED',
        title: 'INTERNET C INTERRUPTED · GAME STAYED ONLINE',
        detail: 'NoPing kept transmitting through Internet A + B. Without simultaneous links, the game would have disconnected.',
        aria: 'Internet C was interrupted. NoPing continued transmitting through Internets A and B and prevented the game from disconnecting.',
      },
      i12: {
        indicator: 'A + B INTERRUPTED',
        title: 'INTERNET A + B INTERRUPTED · GAME STAYED ONLINE',
        detail: 'NoPing kept transmitting through Internet C. Without simultaneous links, the game would have disconnected.',
        aria: 'Internets A and B were interrupted. NoPing continued transmitting through Internet C and kept the game connected.',
      },
      i13: {
        indicator: 'A + C INTERRUPTED',
        title: 'INTERNET A + C INTERRUPTED · GAME STAYED ONLINE',
        detail: 'NoPing kept transmitting through Internet B. Without simultaneous links, the game would have disconnected.',
        aria: 'Internets A and C were interrupted. NoPing continued transmitting through Internet B and kept the game connected.',
      },
      i23: {
        indicator: 'B + C INTERRUPTED',
        title: 'INTERNET B + C INTERRUPTED · GAME STAYED ONLINE',
        detail: 'NoPing kept transmitting through Internet A. Without simultaneous links, the game would have disconnected.',
        aria: 'Internets B and C were interrupted. NoPing continued transmitting through Internet A and kept the game connected.',
      },
    },
    guideStages: [
      { label: '01 · COMPUTER + 3 INTERNET LINKS', action: 'SWIPE → ALL 9 NOPING SERVERS' },
      { label: '02 · ALL 9 NOPING SERVERS', action: 'SWIPE → FINAL NOPING + GAME' },
      { label: '03 · FINAL NOPING + GAME', action: '← SWIPE BACK · COMPUTER + 3 INTERNETS' },
    ],
    reducedMotionAria: 'Motion reduced. All three internet links and all nine NoPing servers are shown online in a static topology.',
  },
  multiInternet: {
    ispInfo: [
      { label: 'FIBER A', speed: '500 MBPS', latency: '11ms' },
      { label: 'CABLE B', speed: '300 MBPS', latency: '17ms' },
    ],
    phaseCopy: {
      healthy: 'ALL LINKS HEALTHY',
      degrading: 'FIBER A DEGRADING',
      detected: 'FAILURE DETECTED',
      failover: 'TRAFFIC FAILOVER COMPLETE',
      recovering: 'LINK RECOVERING',
      restored: 'ALL LINKS RESTORED',
    },
    eventCopy: {
      healthy: 'Watching all uplinks in real time',
      degrading: 'Jitter spike isolated on Fiber A',
      detected: 'Fiber A offline · switching route',
      failover: 'Cable B + 5G carrying session seamlessly',
      recovering: 'Fiber A handshake and health check in progress',
      restored: 'Fiber A restored · 3 uplinks synchronized',
    },
    sync: 'SYNC',
    noInterruption: 'NO INTERRUPTION',
    packetsDroppedNone: '0 PACKETS DROPPED',
    carryingTraffic: 'CARRYING TRAFFIC',
    activeMobile: 'ACTIVE',
  },
  beforeAfter: {
    comparisonDivider: 'Comparison divider',
    packetLoss: (percent) => `Packet loss: ${percent.toFixed(1)}%`,
    jitter: (ms, decimals) => `Jitter: ${ms.toFixed(decimals)}ms`,
  },
  experienceShowcase: {
    serverRows: ['US East — Ashburn', 'US West — Los Angeles', 'EU — Frankfurt', 'Asia — Tokyo'],
    connect: 'CONNECT',
    disconnect: 'DISCONNECT',
    scanningRoutes: 'SCANNING ROUTES',
    resettingRoute: 'RESETTING ROUTE',
    scanning9Routes: 'SCANNING 9 ROUTES',
    testing9Servers: 'TESTING 9 SERVERS',
    selectBestRoute: 'SELECT BEST ROUTE',
    routeLockedMs: '9MS ROUTE LOCKED',
    readyToDownload: 'READY TO DOWNLOAD',
    downloadStarted: 'DOWNLOAD STARTED',
    pingOffline: 'PING OFFLINE',
    pingOnline: 'PING ONLINE',
    loweringPingBoostingFps: 'LOWERING PING · BOOSTING FPS',
    games: [
      { name: 'COUNTER-STRIKE 2', short: 'CS2' },
      { name: 'VALORANT', short: 'VALORANT' },
      { name: 'DOTA 2', short: 'DOTA 2' },
      { name: 'FORTNITE', short: 'FORTNITE' },
    ],
    gameQueued: (short) => `${short} QUEUED`,
    gameSelected: (short) => `${short} SELECTED`,
    pingLockedFps: (ms, fps) => `${ms}MS PING · ${fps} FPS`,
    offlinePlaceholder: '--',
  },
  gamesShowcase: {
    connectionUnoptimized: 'CONNECTION UNOPTIMIZED',
    activatingNoping: 'ACTIVATING NOPING',
    nopingOptimized: 'NOPING OPTIMIZED',
    optimizeGame: (gameName) => `Optimize ${gameName}`,
  },
  howItWorks: {
    stepPreviewStatuses: ['INSTALL READY', 'VALORANT SELECTED', '9MS ROUTE LOCKED'],
    waitingForInstall: 'WAITING FOR INSTALL',
    selectingValorant: 'SELECTING VALORANT',
    valorantSelected: 'VALORANT SELECTED',
    waitingForGame: 'WAITING FOR GAME',
    testingRouteOfNine: (routeNumber) => `TESTING ROUTE ${routeNumber}/9`,
    routeLockedNineMs: '9MS ROUTE LOCKED',
  },
  premiumHeroPingDrop: {
    clientChapters: {
      selectFortnite: 'SELECT FORTNITE',
      buildingSmartRoute: 'BUILDING SMART ROUTE',
      sessionConnected: 'SESSION CONNECTED',
      liveTrafficOptimized: 'LIVE TRAFFIC OPTIMIZED',
    },
    rawLatency: 'RAW 168ms',
    smartPending: 'SMART —',
    smartMs: (value) => `SMART ${value}ms`,
    baselineCapture: 'BASELINE CAPTURE',
    unstable: 'UNSTABLE',
    scanning9RoutesSentence: 'Scanning 9 routes',
    poor: 'POOR',
    probingEdges: 'PROBING EDGES',
    optimizingLive: 'OPTIMIZING LIVE',
    rerouting: 'REROUTING',
    improving: 'IMPROVING',
    bypassingLag: 'BYPASSING LAG',
    bypassingCongestion: 'BYPASSING CONGESTION',
    routeLocked: 'ROUTE LOCKED',
    stable: 'STABLE',
    directPeering: 'Edge 04 · direct peering',
    excellent: 'EXCELLENT',
    pathLocked: 'PATH LOCKED',
    optimalPathLocked: 'OPTIMAL PATH LOCKED',
  },
  premiumMultiConnection: {
    providers: ['GOOGLE', 'AKAMAI', 'AZURE', 'GCORE', 'EQUINIX', 'AWS', 'SOFTLAYER', 'LEASEWEB', 'CLOUDFLARE'],
    discoveringRoutes: 'DISCOVERING ROUTES',
    calculating: 'CALCULATING',
    waiting: 'WAITING',
    probeOfNine: (probeNumber) => `Probe ${probeNumber} of 9 · measuring loss, jitter and ping`,
    awaitingRouteLock: 'AWAITING ROUTE LOCK',
    evaluatingCandidates: 'EVALUATING CANDIDATES',
    aiElection: 'AI ELECTION',
    negotiating: 'NEGOTIATING',
    threeViablePaths: 'Three viable paths · validating stability',
    consensusInProgress: 'CONSENSUS IN PROGRESS',
    fastestPathLocked: 'FASTEST PATH LOCKED',
    edgeLockedNineMs: 'EDGE 05 · 9MS',
  },
  premiumMultiInternet: {
    accessNames: ['INTERNET A', 'INTERNET B', 'WIFI 5G'],
    uplinks: {
      i1: { name: 'INTERNET 1', edges: ['EDGE 01', 'EDGE 02', 'EDGE 03'] },
      i2: { name: 'INTERNET 2', edges: ['EDGE 04', 'EDGE 05', 'EDGE 06'] },
      i3: { name: 'INTERNET 3', edges: ['EDGE 07', 'EDGE 08', 'EDGE 09'] },
    },
    scenarioTitles: {
      reducedMotion: 'REDUCED MOTION · ALL LINKS READY',
      internet1Offline: 'INTERNET 1 OFFLINE',
      internet1Recovered: 'INTERNET 1 RECOVERED',
      internet3Offline: 'INTERNET 3 OFFLINE',
      internet3Recovered: 'INTERNET 3 RECOVERED',
      internet2And3Offline: 'INTERNET 2 + 3 OFFLINE',
      fullRedundancyRestored: 'FULL REDUNDANCY RESTORED',
    },
    offlineFailed: 'OFFLINE · FAILED',
    onlineActive: 'ONLINE · ACTIVE',
    isolatedNoSignal: 'ISOLATED · NO SIGNAL',
    activeLatency: (latency) => `ACTIVE · ${latency}`,
    failoverActiveGameOnline: 'FAILOVER ACTIVE · GAME ONLINE',
    fullRedundancyOnline: 'FULL REDUNDANCY ONLINE',
    pauseMotion: 'PAUSE MOTION',
    resumeMotion: 'RESUME MOTION',
  },
  premiumExperience: {
    gameNames: ['VALORANT', 'CS2', 'FORTNITE', 'DOTA 2', 'WORLD OF WARCRAFT', 'FINAL FANTASY XIV'],
    sceneCaptions: ['SELECT A SERVER · CONNECT · WATCH PING FALL', 'FIVE ROUTES RACE · ONE WINNER', 'EVERY NUMBER BACKED BY LIVE PACKETS', 'SELECT ANY OF 3,000+ GAME PROFILES'],
    servers: [
      { name: 'Ashburn', edge: 'ASHBURN 04' },
      { name: 'Los Angeles', edge: 'LOS ANGELES 12' },
      { name: 'Frankfurt', edge: 'FRANKFURT 07' },
    ],
    ready: 'READY',
    selected: 'SELECTED',
    connectNow: 'CONNECT NOW',
    optimizing: 'OPTIMIZING',
    connectedMs: (ping) => `CONNECTED · ${ping}MS`,
    connected: 'CONNECTED',
    disconnected: 'DISCONNECTED',
    edgeLabel: (edge) => `EDGE · ${edge}`,
    edgeWinsMs: (edge, ping) => `${edge} WINS · ${ping}MS`,
    routeLocked: 'ROUTE LOCKED',
    routeRace: 'ROUTE RACE',
    testing5PathsInParallel: 'TESTING 5 PATHS IN PARALLEL',
    sessionProof: 'SESSION PROOF',
    searching: 'SEARCHING',
    optimizeGameUpper: (gameName) => `OPTIMIZE ${gameName}`,
  },
};
